import { formatDate } from '@bitstopco/bitstop-theme/helpers';

import type { BoxClearSimulatedItemView } from '@/types';
import type { CashLogStatus } from '@/types/machine';

export const CASH_LOG_STATUS_LABEL: Record<CashLogStatus, string> = {
  provisional: 'Provisional',
  confirmed: 'Confirmed',
  invalidated: 'Invalidated',
  deposited: 'Deposited',
  deposited_over: 'Deposited Over',
  deposited_under: 'Deposited Under',
  voided: 'Voided',
};

export const DEPOSIT_OUTCOME_STATUSES: CashLogStatus[] = [
  'deposited',
  'deposited_over',
  'deposited_under',
  'voided',
];

export const cashLogStatusLabel = (status: string) =>
  CASH_LOG_STATUS_LABEL[status as CashLogStatus] ?? status.replace(/_/g, ' ');

export const canSetDepositOutcome = (from: string | undefined, to: CashLogStatus) =>
  from !== to && (from === 'confirmed' || DEPOSIT_OUTCOME_STATUSES.includes(from as CashLogStatus));

/**
 * Raw `Status` codes carried in the audit state snapshots → human label. The
 * snapshots store status as an integer (not the `BoxClearStatus` string enum),
 * so "Status 0 → 1" must be mapped or it's meaningless to an operator.
 */
export const STATUS_LABEL_BY_CODE: Record<string, string> = {
  '0': 'Provisional',
  '1': 'Confirmed',
  '2': 'Invalidated',
  '3': 'Deposited',
  '4': 'Deposited Over',
  '5': 'Deposited Under',
  '6': 'Voided',
};

export const auditStatusLabel = (raw: unknown) => STATUS_LABEL_BY_CODE[String(raw)] ?? String(raw);

/**
 * Accessors for athena simulate-endpoint items.
 *
 * The simulate response schema is not yet published in the athena swagger, so
 * `BoxClearSimulatedItemView` is hand-maintained and these accessors read it
 * defensively (original/simulated totals fall back to each other). Once athena
 * documents the schema, tighten the type and simplify these.
 *
 * Transaction counts (DEV-1862) are read without falling back to the other
 * side: a missing count stays `undefined` so the modals hide tx copy rather
 * than show a misleading number.
 */

/**
 * Extract the affected-clears array from a simulate response.
 *
 * The simulate endpoints' response schema is not yet published in the athena
 * swagger, so the envelope is read defensively: `{ data: [...] }` (the standard
 * `ApiResponse` wrapper), `{ items: [...] }`, or a bare array are all accepted.
 * Without this, an envelope mismatch leaves the modal preview permanently empty
 * (it falls back to its "no clears" / "no preview" placeholder and the real
 * placement/rebalance rows never render).
 */
export const simulateItems = (response: unknown): BoxClearSimulatedItemView[] => {
  if (Array.isArray(response)) return response as BoxClearSimulatedItemView[];
  if (response && typeof response === 'object') {
    const { data, items } = response as { data?: unknown; items?: unknown };
    if (Array.isArray(data)) return data as BoxClearSimulatedItemView[];
    if (Array.isArray(items)) return items as BoxClearSimulatedItemView[];
  }
  return [];
};

/** Marks the not-yet-created clear in a create simulation. */
export const isNewClear = (item: BoxClearSimulatedItemView) => item.cash_clear_id === 'NEW';

/** Worth of a clear before the simulated action is applied. */
export const originalWorth = (item: BoxClearSimulatedItemView) =>
  item.original_values?.total_worth_of_bills ?? item.simulated_values?.total_worth_of_bills ?? 0;

/** Worth of a clear after the simulated action is applied. */
export const simulatedWorth = (item: BoxClearSimulatedItemView) =>
  item.simulated_values?.total_worth_of_bills ?? item.original_values?.total_worth_of_bills ?? 0;

/** Transactions in a clear before the simulated action; `undefined` when not reported. */
export const originalTxCount = (item: BoxClearSimulatedItemView) =>
  item.original_values?.transaction_count ?? undefined;

/** Transactions in a clear after the simulated action; `undefined` when not reported. */
export const simulatedTxCount = (item: BoxClearSimulatedItemView) =>
  item.simulated_values?.transaction_count ?? undefined;

/** `12 tx` — the short form the DEV-1611 previews use. */
export const txCount = (count: number) => `${count.toLocaleString()} tx`;

/** Short UTC moment: `Jan 2, 13:00`. */
export const fmtMoment = (iso: string) => formatDate(iso, 'MMM d, HH:mm', { timeZone: 'UTC' });

/** Mid-sentence name for a simulate item: `clear #12` / `the open period`. */
export const clearName = (item: BoxClearSimulatedItemView) =>
  item.box_clear_id != null ? `clear #${item.box_clear_id}` : 'the open period';

/** Sortable timestamp for ordering the affected window (oldest → newest). */
export const itemTime = (item: BoxClearSimulatedItemView) =>
  item.created_at ? new Date(item.created_at).getTime() : 0;

/** `$1,234` style money formatting. */
export const money = (value = 0) => `$${Math.round(value).toLocaleString()}`;
